// Pure mappings between the overview.rs wire shape (WorkspaceOverview) and
// what the landing page renders — same no-React contract as the dashboard's
// stats-mapping.ts, so the tiles and feed compose these instead of each
// re-deriving its own reading of the wire shape.

import type { OverviewSummary, WorkspaceOverview } from "@/api/types";

/** `null` when the window has no responses — "nothing to report" rather
 *  than a misleading 0%. */
export function completionRate(summary: OverviewSummary): number | null {
    return summary.responses === 0 ? null : summary.completed / summary.responses;
}

/** Response-count change vs the previous window. The tile shows it only when
 *  there is something to compare with — not for All time, and not when the
 *  previous window had no responses (then "No earlier data", Results' rule). */
export function responsesDelta(overview: WorkspaceOverview): number {
    return overview.current.responses - overview.previous.responses;
}

/** Normalized-score change vs the previous window, in points of 0..1.
 *  `null` when either side has no average — never coerced to 0, which would
 *  read as "no change" instead of "not comparable". */
export function scorePctDelta(overview: WorkspaceOverview): number | null {
    if (overview.current.averageScorePct === null || overview.previous.averageScorePct === null) {
        return null;
    }
    return overview.current.averageScorePct - overview.previous.averageScorePct;
}

/** "just now" / "5 min ago" / "3 h ago" / "2 d ago", or the locale date once
 *  it's a week out — the recent feed is a glance, so coarse is right. `now`
 *  is a parameter so tests can pin it. An unparseable timestamp comes back
 *  verbatim rather than as "NaN d ago". */
export function timeAgo(iso: string, now: Date = new Date()): string {
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return iso;
    const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`;
    if (seconds < 7 * 86_400) return `${Math.floor(seconds / 86_400)} d ago`;
    return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
